import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDevice, devicesAPI } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Tabs } from '@/components/ui';
import { LoadingSpinner } from '@/components/common';
import { ArrowLeft, RotateCcw, RefreshCw, Wifi, Shield, Users, Server } from 'lucide-react';

export default function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: deviceData, isLoading, refetch } = useDevice(Number(id));
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<any>(null);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const device = deviceData?.data;

  if (!device) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Терминал не найден</h2>
        <Button onClick={() => navigate('/devices')} className="mt-4">
          Вернуться к списку терминалов
        </Button>
      </div>
    );
  }

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const result = await devicesAPI.testConnection(device.id);
      setConnectionInfo(result.data);
      await refetch();
      alert('Связь с терминалом Hikvision успешно проверена.');
    } catch (error: any) {
      alert(error?.message || 'Не удалось проверить подключение.');
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await devicesAPI.syncHikvision(device.id);
      await refetch();
      alert(`Fallback qo'lda sync tugadi. Yaratildi: ${result.data?.created ?? 0}, dublikat: ${result.data?.duplicate ?? 0}`);
    } catch (error: any) {
      alert(error?.message || 'Fallback sync xatoligi.');
    } finally {
      setSyncing(false);
    }
  };

  const handleRotateSecret = async () => {
    if (!window.confirm('Обновить секрет устройства?')) {
      return;
    }

    setRotating(true);
    try {
      await devicesAPI.rotateSecret(device.id);
      alert('Секрет устройства обновлен.');
    } catch (error: any) {
      alert(error?.message || 'Не удалось обновить секрет.');
    } finally {
      setRotating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: string; label: string }> = {
      active: { variant: 'success', label: 'Подключен' },
      inactive: { variant: 'secondary', label: 'Неактивен' },
      maintenance: { variant: 'warning', label: 'Обслуживание' },
      disconnected: { variant: 'destructive', label: 'Нет связи' },
    };
    const statusInfo = variants[status] || { variant: 'secondary', label: status };
    return <Badge variant={statusInfo.variant as any}>{statusInfo.label}</Badge>;
  };

  const tabs = [
    {
      label: 'Основное',
      content: <DetailsTab device={device} connectionInfo={connectionInfo} />,
    },
    {
      label: 'Сотрудники',
      content: <EmployeesTab deviceId={device.id} />,
    },
    {
      label: 'Белый список IP',
      content: <IpAllowlistTab device={device} onRefresh={refetch} />,
    },
  ];

  return (
    <div className="space-y-6 page-shell">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/devices')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{device.device_name}</h1>
          <p className="text-gray-500">
            {device.model || 'DS-K1T341AMF'} - {device.device_sn}
          </p>
        </div>
        {getStatusBadge(device.status)}
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={handleTestConnection} disabled={testing}>
          <Wifi className="w-4 h-4 mr-2" />
          {testing ? 'Проверка...' : 'Проверить связь'}
        </Button>
        <Button onClick={handleSync} disabled={syncing}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {syncing ? 'Fallback sync...' : 'Qo\'lda sync (fallback)'}
        </Button>
        <Button variant="outline" onClick={handleRotateSecret} disabled={rotating}>
          <RotateCcw className="w-4 h-4 mr-2" />
          {rotating ? 'Обновление...' : 'Обновить секрет'}
        </Button>
      </div>

      <Tabs tabs={tabs} />
    </div>
  );
}

function DetailsTab({ device, connectionInfo }: { device: any; connectionInfo: any }) {
  const endpoint = `${device.protocol || 'http'}://${device.ip_address}:${device.port || 80}/ISAPI/AccessControl/AcsEvent?format=json`;
  const integration = connectionInfo?.integration || device.integration;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="w-5 h-5" />
          Конфигурация Hikvision
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Info label="Производитель" value={device.vendor || 'hikvision'} />
          <Info label="Модель" value={device.model || 'DS-K1T341AMF'} />
          <Info label="Филиал" value={device.branch?.name || '-'} />
          <Info label="IP / порт" value={`${device.ip_address || '-'}:${device.port || 80}`} mono />
          <Info label="Протокол" value={`${device.protocol || 'http'} / ${device.auth_type || 'digest'}`} />
          <Info label="Логин" value={device.device_username || 'admin'} mono />
          <Info label="Расположение" value={device.location_description || '-'} />
          <Info
            label="Последняя синхронизация"
            value={device.last_sync_at ? new Date(device.last_sync_at).toLocaleString('ru-RU') : 'Еще не было'}
          />
          <Info
            label="Время последнего события"
            value={device.last_event_at ? new Date(device.last_event_at).toLocaleString('ru-RU') : 'Еще не было'}
          />
          <Info
            label="Последний serialNo"
            value={device.last_event_serial_no?.toString() || '-'}
            mono
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border bg-gray-50 p-4">
            <div className="text-sm text-gray-500 mb-1">Pull sync (fallback, server device'dan tortadi)</div>
            <div className="font-mono break-all text-sm">{integration?.pull_sync_url || endpoint}</div>
            <div className="mt-2 text-xs text-gray-500">
              Bu terminal yozadigan URL emas. Faqat diagnostika yoki qo'lda import uchun ishlatiladi.
            </div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="text-sm text-blue-700 mb-1">Push webhook (asosiy usul)</div>
            <div className="font-mono break-all text-sm">{integration?.push_webhook_url || '-'}</div>
            {integration?.push_webhook_token && (
              <div className="mt-2 text-xs text-blue-700">
                Terminal HTTP Monitoring o'zi shu URL'ga push yuboradi. Asosiy oqim shu.
              </div>
            )}
          </div>
        </div>

        {connectionInfo && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm">
            <div className="font-medium text-green-800">So'nggi fallback tekshiruv</div>
            <div className="mt-2">Topilgan eventlar: {connectionInfo.events_found ?? 0}</div>
            <div className="font-mono break-all mt-1">{connectionInfo.device_url}</div>
          </div>
        )}

        {device.last_error_message && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <div className="font-medium mb-1">Последняя ошибка</div>
            <div>{device.last_error_message}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmployeesTab({ deviceId }: { deviceId: number }) {
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const result = await devicesAPI.getEmployees(deviceId);
        if (mounted) {
          setEmployees(result.data || []);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [deviceId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Привязанные сотрудники
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-gray-500">Загрузка...</p>
        ) : employees.length === 0 ? (
          <p className="text-gray-500">К этому терминалу еще не привязан ни один сотрудник.</p>
        ) : (
          <div className="space-y-3">
            {employees.map((binding: any) => (
              <div key={binding.id} className="rounded-lg border p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{binding.employee?.full_name || 'Неизвестный сотрудник'}</div>
                  <div className="text-sm text-gray-500">
                    ID пользователя на устройстве: <span className="font-mono">{binding.device_user_id}</span>
                  </div>
                </div>
                <Badge variant={binding.is_active ? 'success' : 'secondary'}>
                  {binding.is_active ? 'Активен' : 'Неактивен'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function IpAllowlistTab({
  device,
  onRefresh,
}: {
  device: any;
  onRefresh: () => Promise<any>;
}) {
  const allowlist = device.ip_allowlist || [];
  const [ipAddress, setIpAddress] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!ipAddress.trim()) {
      return;
    }

    setSaving(true);
    try {
      await devicesAPI.addIpAllowlist(device.id, { ip_address: ipAddress.trim() });
      setIpAddress('');
      await onRefresh();
    } catch (error: any) {
      alert(error?.message || 'Не удалось добавить IP.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (allowlistId: number) => {
    if (!window.confirm('Удалить этот IP из белого списка?')) {
      return;
    }

    try {
      await devicesAPI.removeIpAllowlist(device.id, allowlistId);
      await onRefresh();
    } catch (error: any) {
      alert(error?.message || 'Не удалось удалить IP.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Белый список IP
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="Например: 192.168.1.50"
            value={ipAddress}
            onChange={(e) => setIpAddress(e.target.value)}
          />
          <Button onClick={handleAdd} disabled={saving}>
            Добавить
          </Button>
        </div>

        {allowlist.length === 0 ? (
          <p className="text-gray-500">Пока ограничений по IP нет.</p>
        ) : (
          <div className="space-y-2">
            {allowlist.map((ip: any) => (
              <div
                key={ip.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded border font-mono"
              >
                <span>{ip.ip_address}</span>
                <Button size="sm" variant="ghost" onClick={() => handleRemove(ip.id)}>
                  Удалить
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Info({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <label className="text-sm text-gray-500">{label}</label>
      <p className={`font-medium ${mono ? 'font-mono break-all' : ''}`}>{value}</p>
    </div>
  );
}


